import { cloneElement, isValidElement, useId, type ReactElement } from 'react';
import { Label } from '../primitives/label';
import { cn } from '../lib/cn';

interface FormFieldProps {
  label: string;
  /** Short help text under the label */
  hint?: string;
  /** Validation message (e.g. from react-hook-form + Zod) */
  error?: string;
  required?: boolean;
  className?: string;
  /** One input element; it gets the id and aria attributes automatically */
  children: ReactElement<Record<string, unknown>>;
}

/** Label, input, hint and error wired together for screen readers. */
export function FormField({ label, hint, error, required, className, children }: FormFieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  const control = isValidElement(children)
    ? cloneElement(children, {
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
        'aria-required': required || undefined,
      })
    : children;

  return (
    <div className={cn('grid gap-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {required && (
          <span className="ml-0.5 text-danger" aria-hidden>
            *
          </span>
        )}
      </Label>
      {hint && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
      {control}
      {error && (
        <p id={errorId} role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
